import { withAuthenticationRequired } from '@auth0/auth0-react';
import { LDContext, useLDClient } from 'launchdarkly-react-client-sdk';
import isEqual from 'lodash/isEqual';
import React, { Suspense, lazy, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Navigate } from 'react-router-dom';
import { useEffectOnce } from 'react-use';

import { LoadingLogo } from '@fmbt/ui/LoadingSpinner';
// Don't lazily import this or production build will fail
import ProtectedRoutesLoadingGate from 'components/loadingGates/ProtectedRoutesLoadingGate';
import { ErrorBoundary, SentryRoute as Route, SentryRoutes as Routes, useSetSentryUser } from 'config/sentry';
import BackOffice from 'routes/BackOffice';
import { loadMyInvitations } from 'store/modules/invitations/actions';
import { useMyUser, useMyUserIsInternalAdmin } from 'store/modules/myUser/hooks';
import {
  selectHasOnlyOneMembership,
  selectMyUserMembershipsEnterprises
} from 'store/modules/myUser/selectors';
import BackOfficeInvitation from 'types/models/back-office-invitation';
import constructEnterpriseDomain, { InputForEnterpriseDomain } from 'utils/construct-enterprise-domain';
import getCurrentSubdomain from 'utils/get-current-subdomain';

import Integrations from './Integrations';
import InstallAgriWebb from './Integrations/AgriWebb/Install';
import NewUser from './NewUser';

const Enterprise = lazy(() => import('routes/Enterprise'));
const HardReload = lazy(() => import('routes/HardReload'));
const MyMemberships = lazy(() => import('routes/MyMemberships'));
const PageNotFound = lazy(() => import('routes/PageNotFound'));

const { VITE_URL_DEFAULT_SUBDOMAIN } = import.meta.env;

// ==============================
// HELPERS
// ==============================
const useLoadMyUserInvitations = ({ skip }: { skip: boolean }): BackOfficeInvitation[] | undefined => {
  const dispatch = useDispatch();
  const invitations: BackOfficeInvitation[] | undefined = useMyUser()?.invitations;

  useEffectOnce(() => {
    if (!skip) {
      dispatch(loadMyInvitations());
    }
  });

  return invitations;
};

const RedirectToEnterprise = ({ enterprise }: { enterprise: InputForEnterpriseDomain }) => {
  const url = constructEnterpriseDomain(enterprise);
  useEffect(() => {
    window.location.replace(url);
  }, [url]);
  return null;
};

const lazyLoaded = (component: React.ReactNode) => (
  <Suspense fallback={<LoadingLogo />}>
    {component}
  </Suspense>
);

// ==============================
// ROUTING LOGIC
// ==============================
function MainRoutes(): JSX.Element | null {
  const [isFeatureFlagReady, setIsFeatureFlagReady] = useState(false);

  // ==================== Start: THIS BLOCK ALLOWS FOR FEATURE FLAGS TO WORK ====================
  const ldClient = useLDClient();
  const myUser = useMyUser();
  useSetSentryUser(myUser);

  useEffectOnce(() => {
    if (ldClient && myUser) {
      // We need a context for LaunchDarkly to evaluate and return the feature flags to the application.
      // Within an enterprise domain, we use the enterprise (see EnterpriseScaffoldRequest).
      // In other cases, we use the user.
      const ldContext: LDContext = {
        kind: 'user',
        key: myUser.id.toString(),
        name: myUser.email || myUser.mobileNumberFormatted || myUser.fullName
      };
      ldClient.identify(ldContext).then(() => setIsFeatureFlagReady(true));
    }
  });
  // ==================== End: THIS BLOCK ALLOWS FOR FEATURE FLAGS TO WORK ====================

  const myUserIsInternalAdmin = useMyUserIsInternalAdmin();
  const hasOnlyOneMembership = useSelector(selectHasOnlyOneMembership);
  const currentSubdomain = getCurrentSubdomain();
  const isAccessingSpecificEnterprise = Boolean(
    currentSubdomain && currentSubdomain !== VITE_URL_DEFAULT_SUBDOMAIN
  );
  const pendingInvitations: BackOfficeInvitation[] | undefined =
    useLoadMyUserInvitations({ skip: isAccessingSpecificEnterprise });
  const myUserMembershipsEnterprises = useSelector(
    selectMyUserMembershipsEnterprises,
    isEqual
  );

  if (!isFeatureFlagReady) {
    return <LoadingLogo />;
  }

  if (isAccessingSpecificEnterprise) {
    return lazyLoaded(<Enterprise />);
  }

  // Undefined means not-yet loaded
  if (!pendingInvitations) {
    return <LoadingLogo />;
  }

  // (Unless on a specific enterprise subdomain)
  // If having pending invitations, the user must resolve them first (Join or Delete) before all other operations.
  // This ensures:
  // 1. The user does not forget about open invitations.
  // 2. The user is not redirected to an existing enterprise while trying to join a new one, e.g., https://farmbot.atlassian.net/browse/FMBT-7750
  if (pendingInvitations.length > 0) {
    return (
      <Routes>
        <Route path="enterprises" element={lazyLoaded(<MyMemberships />)} />
        <Route path="*" element={<Navigate replace to="enterprises" />} />
      </Routes>
    );
  }

  if (myUserIsInternalAdmin) {
    return (
      <Routes>
        <Route path="back-office/*" element={<BackOffice />} />
        <Route path="connections/*" element={<Integrations />} />
        <Route path="*" element={<Navigate replace to="back-office" />} />
      </Routes>
    );
  }

  if (hasOnlyOneMembership) {
    return (
      <Routes>
        <Route path="enterprises" element={lazyLoaded(<MyMemberships />)} />
        <Route path="connections/*" element={<Integrations />} />
        <Route path="*" element={<RedirectToEnterprise enterprise={myUserMembershipsEnterprises[0]} />} />
      </Routes>
    );
  }

  return (
    <Routes>
      <Route path="connections/*" element={<Integrations />} />
      <Route path="*" element={lazyLoaded(<MyMemberships />)} />
    </Routes>
  );
}

const ProtectedRoutes = withAuthenticationRequired(
  () => (
    <ProtectedRoutesLoadingGate>
      <MainRoutes />
    </ProtectedRoutesLoadingGate>
  ),
  {
    onRedirecting: () => (
      <LoadingLogo />
    )
  }
);

const AllRoutes = () => (
  <ErrorBoundary>
    <Routes>
      {/* FIX: subdomain can access this link too */}
      <Route path="invite/:token" element={<NewUser />} />
      {/* Heads-up: This route is public, unlike those in src/routes/Integrations/index */}
      <Route path="connections/agriwebb/install" element={<InstallAgriWebb />} />
      <Route path="404" element={lazyLoaded(<PageNotFound />)} />
      <Route path="clear" element={lazyLoaded(<HardReload />)} />
      <Route path="*" element={<ProtectedRoutes />} />
    </Routes>
  </ErrorBoundary>
);

export default AllRoutes;
